import { dealPromptFlags } from '@openx/constants/feature-flag-keys';
import type { AudienceSegmentEntry, InventoryBundle, InventoryDomain } from '@openx/types/gqlTypes';
import {
  type AllTableCriteria,
  type CriteriaChange,
  CriteriaDimension,
  SortDirection,
} from '@openx/types/tableCriteria';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDealPrompt } from '../../../hooks/useDealPrompt';
import { segments } from '../../../utils/getTilesData';
import { type DealPromptContextType, type DealPromptFilters, dealPromptContextTypes } from '../../../utils/types';
import { itemMatchesSearch } from '../utils';

const PAGE_SIZE = 25;

const SORTABLE_CONTEXT_COLUMNS = {
  [dealPromptContextTypes.DOMAINS]: ['domainName'],
  [dealPromptContextTypes.APPS]: ['bundleId', 'platform'],
  [dealPromptContextTypes.AUDIENCE]: [
    'name',
    'dataPartner',
    'price',
    'activeIps',
    'mobileDevices',
    'uniqueUsers',
    'potentialSpend',
  ],
};

const DEFAULT_SORTING_COLUMN_BY_SEGMENT_TYPE: Record<string, string> = {
  [segments.BEST_SCALE]: 'totalImpressions',
  [segments.BEST_SCALE_DEPRECATED]: 'totalImpressions',
  [segments.BEST_RELEVANCE]: 'compositeScore',
  [segments.BEST_PERFORMANCE]: 'compositeScore',
  [segments.BEST_PERFORMANCE_AUDIENCE]: 'totalImpressions',
  [segments.HIGHEST_SCALE]: 'totalImpressions',
  [segments.MOST_RELEVANT]: 'compositeScore',
};

const DEFAULT_SORT_COLUMN_BY_CONTEXT: Partial<Record<DealPromptContextType, string>> = {
  [dealPromptContextTypes.DOMAINS]: 'relevance',
  [dealPromptContextTypes.APPS]: 'relevance',
};

const DEFAULT_SORT_COLUMN = 'totalImpressions';

const DEFAULT_PAGINATION = {
  pageNumber: 1,
  pageSize: PAGE_SIZE,
  totalCount: 100,
} as const;

const ADDITIONAL_SORTABLE_COLUMNS = [
  'totalBidSpendInUsd',
  'totalCprMarketRequests',
  'relevance',
  'totalImpressions',
  'avgBidCpm',
  'avgViewability',
  'avgPubWinRate',
  'avgOxWinRate',
] as const;

export const getDefaultTableCriteria = (
  contextType: DealPromptContextType,
  segmentType?: string,
  isResultsUIV2Enabled = false
): AllTableCriteria => {
  const v2SortColumn = isResultsUIV2Enabled ? DEFAULT_SORT_COLUMN_BY_CONTEXT[contextType] : undefined;
  const sortColumn =
    v2SortColumn || (segmentType && DEFAULT_SORTING_COLUMN_BY_SEGMENT_TYPE[segmentType]) || DEFAULT_SORT_COLUMN;

  return {
    [CriteriaDimension.SORT]: {
      column: sortColumn,
      direction: SortDirection.DESC,
      sortableColumns: [...SORTABLE_CONTEXT_COLUMNS[contextType], ...ADDITIONAL_SORTABLE_COLUMNS],
    },
    [CriteriaDimension.PAGINATION]: DEFAULT_PAGINATION,
    [CriteriaDimension.FILTERS]: {
      segment_search_mode: undefined,
      account: undefined,
    },
  };
};

export const useDealPromptDetailsTableResults = (
  items: InventoryDomain[] | InventoryBundle[] | AudienceSegmentEntry[],
  search: string
) => {
  const {
    state: { contextType, selectedTile },
  } = useDealPrompt();

  const { isGrowthbookFeatureOn } = usePermissionContext();
  const isResultsUIV2Enabled = isGrowthbookFeatureOn(dealPromptFlags.DEAL_PROMPT_RESULTS_UI_V2);

  const segment = useMemo(() => {
    if (selectedTile?.item && 'segment' in selectedTile.item) {
      return selectedTile.item.segment;
    }
    return undefined;
  }, [selectedTile?.item]);

  const defaultTableCriteria = getDefaultTableCriteria(contextType, segment, isResultsUIV2Enabled);

  const [criteria, setCriteria] = useState<AllTableCriteria<DealPromptFilters>>({
    ...defaultTableCriteria,
    pagination: { ...defaultTableCriteria.pagination!, totalCount: items.length },
  });

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension>) => {
      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);

      if (!isEqual(newCriteriaProcessed, criteria)) {
        setCriteria(newCriteriaProcessed);
      }
    },
    [criteria]
  );

  const unsortedItems = useMemo(
    () => items.filter(item => itemMatchesSearch(item, search, contextType)),
    [items, search, contextType]
  );

  const results = useMemo(() => {
    let data = unsortedItems;

    if (criteria.sort) {
      data = orderBy(
        data,
        [
          data => {
            const key = criteria.sort?.column as keyof InventoryDomain | keyof InventoryBundle;
            const value = data[key];

            if (key === 'totalBidSpendInUsd') {
              return parseInt(value, 10);
            }

            return typeof value === 'string' ? value.toLowerCase() : value;
          },
        ],
        [criteria.sort?.direction]
      );
    }
    return data;
  }, [unsortedItems, criteria]);

  const paginatedResults = useMemo(() => {
    let data = results;

    if (criteria.pagination) {
      const start = (criteria.pagination.pageNumber - 1) * criteria.pagination.pageSize;
      const end = start + criteria.pagination.pageSize;
      data = data.slice(start, end);
    }

    return data;
  }, [criteria, results]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: set new criteria only when search or sort changes
  useEffect(() => {
    setCriteria(prev => ({
      ...prev,
      pagination: { ...prev.pagination!, totalCount: unsortedItems.length, pageNumber: 1 },
    }));
  }, [criteria.sort, search]);

  return {
    totalResults: results,
    results: paginatedResults,
    tableCriteria: criteria,
    handleCriteriaChange,
  };
};
